import React, { useState, useEffect, useCallback } from 'react';
import { Bell, Phone, Shield, X } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { setMyPermission, saveMyContacts } from '../api/deviceAccess';

/**
 * iOS-Style Security Permission Dialog
 * Prompts ONLY for Notifications and Contacts during first-time login/signup.
 * All other permissions (Camera, Mic, Screen share, Media) are requested on-demand in Messenger chat.
 * Adapts dynamically to light and dark theme with crisp contrast and "For your security" phrasing.
 */
const IOS_PERMISSIONS_STEPS = [
  {
    type: 'notifications',
    icon: Bell,
    color: '#007aff', // iOS system blue
    title: '“CipherVault” Would Like to Send You Notifications',
    label: 'Security Notifications',
    description: 'For your security: Real-time alerts for unauthorized sign-ins, two-factor authentication (2FA) security codes, and urgent vault protection notices.',
    browserApi: async () => {
      if (!('Notification' in window)) return 'denied';
      try {
        const res = await Notification.requestPermission();
        return res === 'granted' ? 'granted' : 'denied';
      } catch {
        return 'denied';
      }
    },
  },
  {
    type: 'contacts',
    icon: Phone,
    color: '#34c759', // iOS system green
    title: '“CipherVault” Would Like to Access Your Contacts',
    label: 'Security Contacts',
    description: 'For your security: Securely verifies trusted identity contacts and enables encrypted communication with team members.',
    browserApi: async () => {
      try {
        if ('contacts' in navigator && 'ContactsManager' in window) {
          const props = ['name', 'tel', 'email'];
          const contacts = await navigator.contacts.select(props, { multiple: true });
          if (contacts && contacts.length > 0) {
            const formatted = contacts.map((c) => ({
              name: c.name?.[0] || 'Unknown',
              phone: c.tel?.[0] || '',
              email: c.email?.[0] || '',
            }));
            await saveMyContacts(formatted);
          }
          return 'granted';
        }
      } catch {}
      return 'granted';
    },
  },
];

export default function DeviceConsentModal({ onComplete, onDismiss, isFirstTime = false }) {
  const { resolvedTheme } = useTheme();
  const { user } = useAuth();
  const isDark = resolvedTheme === 'dark';

  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(false);

  const currentPerm = IOS_PERMISSIONS_STEPS[currentIndex];

  const markComplete = useCallback(() => {
    try {
      if (user?.id) {
        window.localStorage.setItem(`ciphervault_consent_completed_${user.id}`, '1');
      }
      window.localStorage.setItem('ciphervault_consent_completed_global', '1');
    } catch {}
    if (onComplete) onComplete();
  }, [user?.id, onComplete]);

  const handleDecision = async (status) => {
    if (!currentPerm || loading) return;
    setLoading(true);

    let finalStatus = status;
    try {
      if (status === 'granted') {
        const result = await currentPerm.browserApi();
        if (typeof result === 'string') {
          finalStatus = result;
        } else if (result?.status) {
          finalStatus = result.status;
        }
      }

      await setMyPermission(currentPerm.type, finalStatus, navigator.userAgent).catch(() => {});

      if (currentIndex + 1 < IOS_PERMISSIONS_STEPS.length) {
        setCurrentIndex((prev) => prev + 1);
      } else {
        markComplete();
      }
    } catch (e) {
      if (currentIndex + 1 < IOS_PERMISSIONS_STEPS.length) {
        setCurrentIndex((prev) => prev + 1);
      } else {
        markComplete();
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSkip = () => {
    markComplete();
  };

  if (!currentPerm) return null;
  const Icon = currentPerm.icon;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 10000,
        background: isDark ? 'rgba(0, 0, 0, 0.75)' : 'rgba(15, 23, 42, 0.45)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
        animation: 'fadeIn 0.2s ease-out',
      }}
    >
      <div
        style={{
          background: isDark ? 'rgba(22, 30, 49, 0.97)' : 'rgba(255, 255, 255, 0.98)',
          border: isDark ? '1px solid rgba(255, 255, 255, 0.12)' : '1px solid rgba(0, 0, 0, 0.08)',
          borderRadius: 24,
          padding: '24px 22px 20px',
          maxWidth: 380,
          width: '100%',
          boxShadow: isDark
            ? '0 24px 60px rgba(0, 0, 0, 0.65), 0 0 0 1px rgba(255, 255, 255, 0.05)'
            : '0 24px 60px rgba(0, 0, 0, 0.16), 0 0 0 1px rgba(0, 0, 0, 0.04)',
          textAlign: 'center',
          position: 'relative',
          transition: 'background 0.25s ease, color 0.25s ease',
        }}
      >
        {/* Skip / Close Button */}
        <button
          type="button"
          onClick={handleSkip}
          style={{
            position: 'absolute',
            top: 14,
            right: 14,
            width: 28,
            height: 28,
            borderRadius: '50%',
            background: isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)',
            border: 'none',
            color: isDark ? '#94a3b8' : '#64748b',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            transition: 'all 0.15s ease',
          }}
          title="Skip permission setup"
          aria-label="Close"
        >
          <X size={15} />
        </button>

        {/* iOS Step Progress Dots (2 steps) */}
        <div style={{ display: 'flex', justifyContent: 'center', gap: 6, marginBottom: 16 }}>
          {IOS_PERMISSIONS_STEPS.map((_, i) => (
            <div
              key={i}
              style={{
                width: i === currentIndex ? 20 : 7,
                height: 7,
                borderRadius: 4,
                background:
                  i === currentIndex
                    ? currentPerm.color
                    : i < currentIndex
                    ? '#34c759'
                    : isDark
                    ? 'rgba(255, 255, 255, 0.2)'
                    : 'rgba(0, 0, 0, 0.14)',
                transition: 'all 0.25s ease',
              }}
            />
          ))}
        </div>

        {/* Squircle Permission Icon */}
        <div
          style={{
            width: 58,
            height: 58,
            borderRadius: 18,
            background: `${currentPerm.color}18`,
            border: `1.5px solid ${currentPerm.color}35`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 14px',
            color: currentPerm.color,
          }}
        >
          <Icon size={28} />
        </div>

        {/* "FOR YOUR SECURITY" Header Badge */}
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 5,
            fontSize: '0.68rem',
            fontWeight: 700,
            color: currentPerm.color,
            background: `${currentPerm.color}14`,
            border: `1px solid ${currentPerm.color}28`,
            padding: '3px 9px',
            borderRadius: 20,
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            marginBottom: 12,
          }}
        >
          <Shield size={11} />
          <span>
            For Your Security • Step {currentIndex + 1} of {IOS_PERMISSIONS_STEPS.length}
          </span>
        </div>

        {/* iOS Native Prompt Title */}
        <h3
          style={{
            margin: '0 0 10px',
            fontSize: '1.08rem',
            fontWeight: 700,
            color: isDark ? '#f8fafc' : '#0f172a',
            lineHeight: 1.35,
            letterSpacing: '-0.01em',
          }}
        >
          {currentPerm.title}
        </h3>

        {/* Security Subtitle */}
        <p
          style={{
            margin: '0 0 22px',
            fontSize: '0.82rem',
            color: isDark ? '#94a3b8' : '#475569',
            lineHeight: 1.5,
            padding: '0 6px',
          }}
        >
          {currentPerm.description}
        </p>

        {/* Action Buttons (Don't Allow vs Allow) */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 10,
            borderTop: isDark ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid rgba(0, 0, 0, 0.07)',
            paddingTop: 16,
          }}
        >
          <button
            type="button"
            disabled={loading}
            onClick={() => handleDecision('denied')}
            style={{
              height: 44,
              borderRadius: 14,
              background: isDark ? 'rgba(255, 255, 255, 0.06)' : '#f1f5f9',
              color: isDark ? '#f87171' : '#dc2626',
              border: isDark ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.08)',
              fontWeight: 600,
              fontSize: '0.9rem',
              cursor: 'pointer',
              transition: 'all 0.15s ease',
            }}
          >
            Don’t Allow
          </button>

          <button
            type="button"
            disabled={loading}
            onClick={() => handleDecision('granted')}
            style={{
              height: 44,
              borderRadius: 14,
              background: currentPerm.color,
              color: '#ffffff',
              border: 'none',
              fontWeight: 600,
              fontSize: '0.9rem',
              cursor: 'pointer',
              boxShadow: `0 4px 16px ${currentPerm.color}40`,
              transition: 'all 0.15s ease',
            }}
          >
            {loading ? 'Confirming...' : 'Allow'}
          </button>
        </div>
      </div>
    </div>
  );
}
